import { Route } from '@angular/router';
import { Layout } from './ui/layout/layout';
import { authGuard } from './auth/auth.guard';
import { DashboardsLanding } from './features/dashboards-landing/dashboards-landing';
import { unsavedChangesGuard } from './shared/guards/unsaved-changes/unsaved-changes.guard';
import { ProjectSelection } from './features/project-selection/project-selection';
import { ProjectGuard } from './features/project-guard';
import { OverviewsLanding } from './features/overview-landing/overviews-landing';
import { permissionGuard } from './shared/guards/permission-guard/permission.guard';
import {
  CORRECTIONS_ACCESS,
  Permissions,
  REFERENCE_PARTY_KINDS,
  REFERENCE_PARTY_SECTIONS,
  SECTIONS,
} from '@anessa/shared-models';
import { urlIdGuard } from './shared/guards/url-id/url-id.guard';
import { SectionConfigApiService } from './features/data-tables/section-config-api.service';
import { DashboardService } from './features/dashboard/services/dashboard/dashboard.service';
import { ReportService } from './features/report-creation/services/report.service';
import { CustomViewsService } from './features/custom-views/services/custom-views.service';
import { CustomViewsLanding } from './features/custom-views-landing/custom-views-landing';

/** A MotherDB browser page: Données entrantes alone (not the /motherdb parent's wider list). */
const ENTRANTES_ONLY = { section: SECTIONS.DONNEES_ENTRANTES, orSections: [] };

export const appRoutes: Route[] = [
  {
    path: '',
    component: Layout,
    canActivate: [authGuard],
    children: [
      {
        path: '',
        component: ProjectSelection,
      },
      {
        // Fallback landing for roles that can't view dashboards or receptions —
        // deliberately not section-gated (see ProjectService.landingRoute).
        path: 'home',
        canActivate: [ProjectGuard],
        loadComponent: () =>
          import('./features/home-landing/home-landing').then(
            (m) => m.HomeLanding,
          ),
      },
      {
        path: 'overview',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { section: SECTIONS.DASHBOARDS },
        children: [
          {
            path: '',
            component: OverviewsLanding,
          },
          {
            path: ':id',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'id',
                validationService: DashboardService,
                redirectTo: '/overview',
              },
            },
            loadComponent: () =>
              import('./features/overview-page/overview-page').then(
                (m) => m.OverviewPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'dashboard',
        // No blanket section gate here: the pre-made Operations - Site boards
        // are gated by their own board section, while the shared-dashboard
        // collection (landing + :id) carries DASHBOARDS explicitly.
        canActivateChild: [ProjectGuard],
        children: [
          {
            path: '',
            component: DashboardsLanding,
            canActivate: [permissionGuard],
            data: { section: SECTIONS.DASHBOARDS },
          },
          // Static "Operations - Site" boards. Declared before `:id` so the
          // dashboard-id route can never swallow the segment. Each carries its
          // own view-only board section (not DASHBOARDS), so a role can be
          // granted a single board without the whole shared-dashboard region.
          {
            path: 'operations-site/incorporations',
            canActivate: [permissionGuard],
            runGuardsAndResolvers: 'always',
            data: { section: SECTIONS.DASH_INCORPORATIONS },
            loadComponent: () =>
              import(
                './features/incorporations/incorporations-dashboard/incorporations-dashboard'
              ).then((m) => m.IncorporationsDashboard),
          },
          {
            path: 'operations-site/biodec',
            canActivate: [permissionGuard],
            runGuardsAndResolvers: 'always',
            data: { section: SECTIONS.DASH_BIODEC },
            loadComponent: () =>
              import(
                './features/biodec/biodec-dashboard/biodec-dashboard'
              ).then((m) => m.BiodecDashboard),
          },
          {
            path: 'operations-site/stocks-niveaux',
            canActivate: [permissionGuard],
            runGuardsAndResolvers: 'always',
            data: { section: SECTIONS.DASH_STOCKS_NIVEAUX },
            loadComponent: () =>
              import(
                './features/stocks/stocks-dashboard/stocks-dashboard'
              ).then((m) => m.StocksDashboard),
          },
          {
            path: 'operations-site/digestat',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.DASH_DIGESTAT },
            loadComponent: () =>
              import(
                './features/digestat/digestat-dashboard/digestat-dashboard'
              ).then((m) => m.DigestatDashboard),
          },
          {
            path: 'operations-site/biologie-qualite',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.DASH_BIOLOGIE_QUALITE },
            loadComponent: () =>
              import(
                './features/suivi-biologie/biologie-dashboard/biologie-dashboard'
              ).then((m) => m.BiologieDashboard),
          },
          {
            path: 'operations-site/production',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.DASH_PRODUCTION },
            loadComponent: () =>
              import(
                './features/production/production-dashboard/production-dashboard'
              ).then((m) => m.ProductionDashboard),
          },
          {
            path: 'operations-site/energie',
            canActivate: [permissionGuard],
            runGuardsAndResolvers: 'always',
            data: { section: SECTIONS.DASH_PERFORMANCE_ENERGETIQUE },
            loadComponent: () =>
              import(
                './features/energy-dashboard/energy-dashboard-page/energy-dashboard-page'
              ).then((m) => m.EnergyDashboardPage),
          },
          {
            path: ':id',
            canActivate: [permissionGuard, urlIdGuard],
            data: {
              section: SECTIONS.DASHBOARDS,
              urlIdValidation: {
                param: 'id',
                validationService: DashboardService,
                redirectTo: '/dashboard',
              },
            },
            loadComponent: () =>
              import('./features/dashboards-page/dashboards-page').then(
                (m) => m.DashboardsPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'custom-views',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { section: SECTIONS.CUSTOM_VIEWS },
        children: [
          {
            path: '',
            component: CustomViewsLanding,
          },
          {
            path: ':id',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'id',
                validationService: CustomViewsService,
                redirectTo: '/custom-views',
              },
            },
            loadComponent: () =>
              import('./features/custom-views/custom-views-page').then(
                (m) => m.CustomViewsPage,
              ),
            canDeactivate: [unsavedChangesGuard],
          },
        ],
      },
      {
        path: 'user-access-management',
        canActivate: [permissionGuard],
        data: { permission: Permissions.GLOBAL_ADMIN },
        loadComponent: () =>
          import(
            './features/permissions-management/user-access-management/user-access-management'
          ).then((m) => m.UserAccessManagement),
      },
      {
        path: 'audit',
        canActivateChild: [permissionGuard],
        children: [
          {
            path: '',
            data: { permission: Permissions.GLOBAL_ADMIN },
            loadComponent: () =>
              import('./features/audit/audit-page').then((m) => m.AuditPage),
          },
          {
            path: ':id',
            data: { permission: Permissions.GLOBAL_ADMIN },
            loadComponent: () =>
              import('./features/audit/audit-detail/audit-detail').then(
                (m) => m.AuditDetail,
              ),
          },
        ],
      },
      {
        path: 'role-permissions-management',
        canActivate: [permissionGuard],
        data: { permission: Permissions.GLOBAL_ADMIN },
        loadComponent: () =>
          import(
            './features/permissions-management/role-permission-management/role-permission-management'
          ).then((m) => m.RolePermissionManagement),
      },
      {
        path: 'invitations',
        canActivate: [permissionGuard],
        data: { permission: Permissions.GLOBAL_ADMIN },
        loadComponent: () =>
          import('./features/permissions-management/invitations/invitations').then(
            (m) => m.Invitations,
          ),
      },
      {
        path: 'insights',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.MEASURES },
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/metrics-manager/metrics-manager.component').then(
            (m) => m.MetricsManager,
          ),
      },
      {
        path: 'flare-analysis',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { section: SECTIONS.TORCHAGE },
        loadComponent: () =>
          import('./features/flare-analysis/flare-analysis-page').then(
            (m) => m.FlareAnalysisPage,
          ),
      },
      {
        path: 'planning',
        data: { section: SECTIONS.PLANNING },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./features/planning/planning-page/planning-page').then(
                (m) => m.PlanningPage,
              ),
          },
        ],
      },
      {
        path: 'approvisionnements',
        runGuardsAndResolvers: 'always',
        data: { section: SECTIONS.APPROVISIONNEMENTS },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/receptions/receptions-list/receptions-list'
              ).then((m) => m.ReceptionsList),
          },
          {
            path: ':id',
            loadComponent: () =>
              import(
                './features/receptions/reception-detail/reception-detail'
              ).then((m) => m.ReceptionDetail),
          },
        ],
      },
      {
        path: 'production',
        data: { section: SECTIONS.PRODUCTION },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/production/production-input/production-input'
              ).then((m) => m.ProductionInput),
          },
        ],
      },
      {
        path: 'ration-weekly-planning',
        data: { section: SECTIONS.RATION_PLANNING },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/ration-weekly-planning/ration-weekly-planning-page/ration-weekly-planning-page'
              ).then((m) => m.RationWeeklyPlanningPage),
          },
        ],
      },
      {
        path: 'energies-utilites',
        data: { section: SECTIONS.ENERGIES_UTILITES },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            // A plant switch reloads the same URL; this lets the unsaved-changes guard run on it.
            runGuardsAndResolvers: 'always',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/energy-utility/energy-utility-page/energy-utility-page'
              ).then((m) => m.EnergyUtilityPage),
          },
        ],
      },
      {
        path: 'suivi-site',
        data: { section: SECTIONS.SUIVI_SITE },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            // A plant switch reloads the same URL; this lets the unsaved-changes guard run on it.
            runGuardsAndResolvers: 'always',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/site-monitoring/site-monitoring-page/site-monitoring-page'
              ).then((m) => m.SiteMonitoringPage),
          },
        ],
      },
      {
        path: 'suivi-transferts',
        data: { section: SECTIONS.SUIVI_TRANSFERTS, siteMonitoringBoard: 'transfers' },
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            runGuardsAndResolvers: 'always',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/site-monitoring/site-monitoring-page/site-monitoring-page'
              ).then((m) => m.SiteMonitoringPage),
          },
        ],
      },
      {
        // Corrections console (ADEV-1075).
        path: 'corrections',
        // Corrections at edit (ADEV-1207, CORRECTIONS_ACCESS); the page then
        // offers each flow the user can also view (Approvisionnements / Expéditions).
        data: {
          section: SECTIONS.CORRECTIONS,
          sectionLevel: CORRECTIONS_ACCESS.corrections,
        },
        canActivate: [ProjectGuard, permissionGuard],
        loadComponent: () =>
          import(
            './features/procurement/corrections/corrections-page/corrections-page'
          ).then((m) => m.CorrectionsPage),
      },
      {
        path: 'expeditions',
        data: { section: SECTIONS.EXPEDITIONS },
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(
                './features/expeditions/expeditions-list/expeditions-list'
              ).then((m) => m.ExpeditionsList),
          },
          {
            path: ':id',
            loadComponent: () =>
              import(
                './features/expeditions/expedition-detail/expedition-detail'
              ).then((m) => m.ExpeditionDetail),
          },
        ],
      },
      {
        path: 'matieres',
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: 'gisements',
            runGuardsAndResolvers: 'always',
            data: { section: SECTIONS.GISEMENTS },
            loadComponent: () =>
              import('./features/gisements/gisements-page/gisements-page').then(
                (m) => m.GisementsPage,
              ),
          },
        ],
      },
      {
        // Pilotage › Cible Ration & Production. Every page in the group lives under /ration.
        path: 'ration',
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            // « Ration type »: its own section (ADEV-1207). View opens the page
            // read-only; Add Gisement also needs Gisements edit.
            path: 'objectives',
            canDeactivate: [unsavedChangesGuard],
            data: { section: SECTIONS.RATION_TYPE },
            loadComponent: () =>
              import(
                './features/ration/ration-objectives-page/ration-objectives-page'
              ).then((m) => m.RationObjectivesPage),
          },
          {
            path: 'loading-requirement',
            canDeactivate: [unsavedChangesGuard],
            data: { section: SECTIONS.RATION_PLANNING },
            loadComponent: () =>
              import(
                './features/ration/ration-loading-requirement-page/ration-loading-requirement-page'
              ).then((m) => m.RationLoadingRequirementPage),
          },
        ],
      },
      {
        path: 'parametrage',
        canActivate: [ProjectGuard],
        runGuardsAndResolvers: 'always',
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: 'destinations',
            data: { section: SECTIONS.CONCEPTION },
            loadComponent: () =>
              import(
                './features/destinations/destinations-page/destinations-page'
              ).then((m) => m.DestinationsPage),
          },
          {
            path: 'contenants',
            data: { section: SECTIONS.CONTENANTS },
            loadComponent: () =>
              import(
                './features/contenants/contenants-page/contenants-page'
              ).then((m) => m.ContenantsPage),
          },
          {
            // Hypothèses — a child of Caractéristiques du site, so it sits
            // under /parametrage with its siblings. The path may become French
            // later; the route string, the MULTI_PLANT_HIDDEN_ROUTES entry and
            // the i18n label are independent, so a rename touches only those.
            path: 'assumptions',
            data: { section: SECTIONS.ASSUMPTIONS },
            // Unsaved bounds would be lost on leaving; the page aggregates the
            // check across its sections.
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/assumptions/assumptions-page/assumptions-page'
              ).then((m) => m.AssumptionsPage),
          },
          {
            path: 'informations-site',
            data: { section: SECTIONS.INFORMATIONS_SITE },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import(
                './features/informations-site/informations-site-page/informations-site-page'
              ).then((m) => m.InformationsSitePage),
          },
        ],
      },
      {
        path: 'incorporations',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: 'saisie',
            canDeactivate: [unsavedChangesGuard],
            data: { section: SECTIONS.INCORPORATIONS },
            loadComponent: () =>
              import(
                './features/incorporations/incorporation-saisie/incorporation-saisie'
              ).then((m) => m.IncorporationSaisie),
          },
          // Moved to its own item and section, /biodec (sidebar V2, ADEV-1046).
          { path: 'saisie-biodec', redirectTo: '/biodec' },
          {
            path: 'dilutions',
            data: { section: SECTIONS.INCORPORATIONS },
            loadComponent: () =>
              import(
                './features/dilution-incorporations/dilution-incorporation-entry/dilution-incorporation-entry'
              ).then((m) => m.DilutionIncorporationEntry),
          },
          {
            path: 'detail',
            data: { section: SECTIONS.INCORPORATIONS },
            loadComponent: () =>
              import(
                './features/incorporations/incorporation-detail/incorporation-detail'
              ).then((m) => m.IncorporationDetail),
          },
          {
            path: 'suivi-hebdomadaire',
            data: { section: SECTIONS.INCORPORATIONS },
            loadComponent: () =>
              import(
                './features/incorporations/incorporation-weekly/incorporation-weekly'
              ).then((m) => m.IncorporationWeekly),
          },
        ],
      },
      {
        path: 'stocks',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard],
        canActivateChild: [ProjectGuard, permissionGuard],
        children: [
          {
            path: 'composition',
            data: { section: SECTIONS.STOCKS },
            loadComponent: () =>
              import(
                './features/stocks/stocks-composition/stocks-composition'
              ).then((m) => m.StocksComposition),
          },
          {
            path: 'statut',
            data: { section: SECTIONS.STOCKS },
            loadComponent: () =>
              import('./features/stocks/stocks-status/stocks-status').then(
                (m) => m.StocksStatus,
              ),
          },
        ],
      },
      {
        // Digestat operator input (Operations section in the sidebar). The
        // projected-stock dashboard is a separate board under
        // /dashboard/operations-site/digestat (Tableaux de bord region).
        path: 'digestat',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.DIGESTAT },
        loadComponent: () =>
          import('./features/digestat/digestat-saisie/digestat-saisie').then(
            (m) => m.DigestatSaisie,
          ),
      },
      {
        // Operator checklist (Exploitation › Gestion des flux, ADEV-1041).
        path: 'checklist-operateur',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        canDeactivate: [unsavedChangesGuard],
        data: { section: SECTIONS.CHECKLIST_OPERATEUR },
        loadComponent: () =>
          import('./features/operator-checklist/operator-checklist-page/operator-checklist-page').then(
            (m) => m.OperatorChecklistPage,
          ),
      },
      {
        // RED II compliance report (Réglementaire & Conformité). Table 1 for now.
        path: 'red',
        canActivate: [ProjectGuard, permissionGuard],
        canDeactivate: [unsavedChangesGuard],
        // A plant switch reloads the same URL; without this the guard above
        // doesn't run and staged RED edits are dropped silently.
        runGuardsAndResolvers: 'always',
        data: { section: SECTIONS.RED },
        loadComponent: () =>
          import('./features/red/red-page/red-page').then((m) => m.RedPage),
      },
      {
        // Suivi biologie operator input (Exploitation › Pilotage). One group per
        // biology tank configured on Configuration › Conception, one input per measurement.
        path: 'suivi-biologie',
        canActivate: [ProjectGuard, permissionGuard],
        // Unsaved readings would be lost on leaving; the page's own datepicker
        // prompts with the same dialog.
        canDeactivate: [unsavedChangesGuard],
        data: { section: SECTIONS.SUIVI_BIOLOGIE },
        loadComponent: () =>
          import(
            './features/suivi-biologie/biology-readings-page/biology-readings-page'
          ).then((m) => m.BiologyReadingsPage),
      },
      {
        // Suivi ration (Exploitation › Pilotage). Read-only weekly view.
        path: 'suivi-ration',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.SUIVI_RATION },
        loadComponent: () =>
          import(
            './features/ration-monitoring/ration-monitoring-page/ration-monitoring-page'
          ).then((m) => m.RationMonitoringPage),
      },
      {
        // Biodéconditionnement entry (Exploitation › Gestion des flux): its own
        // item and section since sidebar V2 (ADEV-1046); was
        // /incorporations/saisie-biodec, which redirects here.
        path: 'biodec',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.BIODEC },
        loadComponent: () =>
          import('./features/biodec/biodec-page/biodec-page').then(
            (m) => m.BiodecPage,
          ),
      },
      {
        // Incorporation objectives (Cible Ration & Production › Objectifs). Its
        // own section since sidebar V2 (ADEV-1046), backfilled from each role's
        // Incorporations level by migration 005; the three backend objectives
        // modules require the same section.
        path: 'objectives',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.OBJECTIFS },
        loadComponent: () =>
          import(
            './features/objectives/objectives-page/objectives-page'
          ).then((m) => m.ObjectivesPage),
      },
      {
        // Synoptique (Exploitation › Pilotage): background diagram + admin-
        // positioned data nodes (V0.5 — static label/value, no live data yet).
        path: 'synoptique',
        canActivate: [ProjectGuard, permissionGuard],
        // Unsaved layout edits (drag/add/remove/label) would be lost on
        // leaving while mid-edit — same guard as suivi-biologie above.
        canDeactivate: [unsavedChangesGuard],
        data: { section: SECTIONS.SYNOPTIQUE },
        loadComponent: () =>
          import('./features/synoptic/synoptic-page/synoptic-page').then(
            (m) => m.SynopticPage,
          ),
      },
      {
        path: 'motherdb',
        runGuardsAndResolvers: 'always',
        canActivate: [permissionGuard],
        // Données entrantes (ADEV-1192), Données sortantes (the weighbridge files)
        // or a Données Site party page (ADEV-1207). Each child narrows it to its
        // own section; `orSections: []` stops it inheriting this list.
        data: {
          section: SECTIONS.DONNEES_ENTRANTES,
          orSections: [SECTIONS.DONNEES_SORTANTES, ...Object.values(REFERENCE_PARTY_SECTIONS)],
        },
        children: [
          {
            path: '',
            canActivate: [permissionGuard],
            data: ENTRANTES_ONLY,
            loadComponent: () =>
              import(
                './features/motherdb/motherdb-landing/motherdb-landing'
              ).then((m) => m.MotherdbLanding),
          },
          {
            path: 'plants',
            canActivate: [permissionGuard],
            data: ENTRANTES_ONLY,
            loadComponent: () =>
              import(
                './features/motherdb/motherdb-plants/motherdb-plants'
              ).then((m) => m.MotherdbPlants),
          },
          {
            // The generated weighbridge reference files (Produit/Tiers/…), the
            // combined content dropped to the scale: « Fichiers pont bascule »
            // under Données sortantes (ADEV-1207).
            path: 'reference-files/:kind',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.DONNEES_SORTANTES, orSections: [] },
            loadComponent: () =>
              import(
                './features/procurement/reference-file-view/reference-file-view'
              ).then((m) => m.ReferenceFileView),
          },
          // Unified producer / supplier / transporter lists: Wintrans + Regener +
          // Ecovalim merged, with a source column and a "relevant only" toggle.
          // Producteurs / Fournisseurs / Transporteurs belong to Données Site,
          // one section each (ADEV-1207), so one route per kind.
          ...REFERENCE_PARTY_KINDS.map((kind) => ({
            path: `reference-parties/${kind}`,
            canActivate: [permissionGuard],
            data: { section: REFERENCE_PARTY_SECTIONS[kind], orSections: [], kind },
            loadComponent: () =>
              import(
                './features/reference-parties/reference-parties-page/reference-parties-page'
              ).then((m) => m.ReferencePartiesPage),
          })),
          {
            path: 'sync-status',
            canActivate: [permissionGuard],
            // A capability, not a section: clear the parent's inherited section,
            // or the guard checks that instead.
            data: {
              permission: Permissions.VIEW_MOTHERDB_SYNC_STATUS,
              section: undefined,
              orSections: [],
            },
            loadComponent: () =>
              import(
                './features/motherdb/motherdb-sync-status/motherdb-sync-status'
              ).then((m) => m.MotherdbSyncStatus),
          },
          {
            path: 'collections/:id',
            canActivate: [permissionGuard],
            data: ENTRANTES_ONLY,
            loadComponent: () =>
              import(
                './features/motherdb/motherdb-browser-page/motherdb-browser-page'
              ).then((m) => m.MotherdbBrowserPage),
          },
        ],
      },
      {
        path: 'data-management',
        canActivate: [permissionGuard],
        canActivateChild: [ProjectGuard],
        data: { section: SECTIONS.TABLE_DATA },
        children: [
          {
            path: 'weighbridge/data',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.APPROVISIONNEMENTS },
            loadComponent: () =>
              import(
                './features/weighbridge/weighbridge-data/weighbridge-data-page'
              ).then((m) => m.WeighbridgeDataPage),
          },
          {
            path: 'weighbridge/data/:id',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.APPROVISIONNEMENTS },
            loadComponent: () =>
              import(
                './features/weighbridge/weighbridge-data-detail/weighbridge-data-detail'
              ).then((m) => m.WeighbridgeDataDetail),
          },
          {
            path: 'weighbridge/mapping',
            canActivate: [permissionGuard],
            data: { section: SECTIONS.APPROVISIONNEMENTS },
            loadComponent: () =>
              import(
                './features/data-tables/weighbridge-stub/weighbridge-stub'
              ).then((m) => m.WeighbridgeStub),
          },
          {
            path: ':section',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'section',
                validationService: SectionConfigApiService,
                redirectTo: '/data-management',
              },
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/data-tables/dynamic-section-page').then(
                (m) => m.DynamicSectionPage,
              ),
          },
          { path: '', redirectTo: 'clients', pathMatch: 'full' },
        ],
      },
      {
        path: 'timeseries-data',
        canActivate: [ProjectGuard, permissionGuard],
        data: { section: SECTIONS.TIMESERIES },
        children: [
          {
            path: ':tableId',
            loadComponent: () =>
              import(
                './features/timeseries-data/timeseries-data-page/timeseries-data-page'
              ).then((m) => m.TimeSeriesDataPage),
          },
          {
            path: '',
            loadComponent: () =>
              import(
                './features/timeseries-data/timeseries-data-page/timeseries-data-page'
              ).then((m) => m.TimeSeriesDataPage),
          },
        ],
      },
      {
        path: 'sensor-meta-editor',
        canActivate: [permissionGuard],
        data: { permission: Permissions.VIEW_SENSOR_META_EDITOR },
        loadComponent: () =>
          import('./features/sensor-meta-editor/sensor-meta-editor').then(
            (m) => m.SensorMetaEditor,
          ),
        canDeactivate: [unsavedChangesGuard],
      },
      {
        path: 'metrics-status',
        runGuardsAndResolvers: 'always',
        canActivate: [ProjectGuard, permissionGuard],
        data: { permission: Permissions.RECOMPUTE_METRICS },
        loadComponent: () =>
          import('./features/metrics-status/metrics-status-page').then(
            (m) => m.MetricsStatusPage,
          ),
      },
      {
        path: 'project-settings',
        canActivate: [ProjectGuard],
        canDeactivate: [unsavedChangesGuard],
        loadComponent: () =>
          import('./features/project-settings/project-settings-page').then(
            (m) => m.ProjectSettingsPage,
          ),
      },
      {
        path: 'plants',
        loadComponent: () =>
          import('./features/project-selection/project-selection').then(
            (m) => m.ProjectSelection,
          ),
      },
      {
        path: 'reports',
        canActivate: [ProjectGuard, permissionGuard],
        runGuardsAndResolvers: 'always',
        data: { section: SECTIONS.REPORTING },
        children: [
          {
            path: '',
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/report-creation/report-creation').then(
                (m) => m.ReportCreation,
              ),
          },
          {
            path: ':reportId',
            canActivate: [urlIdGuard],
            data: {
              urlIdValidation: {
                param: 'reportId',
                validationService: ReportService,
                redirectTo: '/reports',
              },
            },
            canDeactivate: [unsavedChangesGuard],
            loadComponent: () =>
              import('./features/report-creation/report-creation').then(
                (m) => m.ReportCreation,
              ),
          },
        ],
      },
    ],
  },
  { path: '**', redirectTo: '/', pathMatch: 'full' },
];
